import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const CurrencyCard = ({ currency, onDeposit, onWithdraw, onTransfer }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [transferAmount, setTransferAmount] = useState('');

  const handleTransfer = () => {
    if (transferAmount && parseFloat(transferAmount) > 0) {
      onTransfer(parseFloat(transferAmount));
      setTransferAmount('');
    }
  };

  const getChangeColor = (change) => {
    if (change > 0) return 'text-success';
    if (change < 0) return 'text-error';
    return 'text-text-secondary';
  };

  const getChangeIcon = (change) => {
    if (change > 0) return 'TrendingUp';
    if (change < 0) return 'TrendingDown';
    return 'Minus';
  };

  return (
    <div className="bg-surface rounded-trading border border-border p-6 hover:shadow-trading-card transition-all duration-trading-fast">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <Image
            src={currency.icon}
            alt={currency.name}
            className="w-10 h-10 rounded-full"
          />
          <div>
            <h3 className="font-heading-semibold text-text-primary">
              {currency.symbol}
            </h3>
            <p className="text-sm text-text-secondary">{currency.name}</p>
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-2 rounded-trading hover:bg-secondary-50 transition-colors duration-trading-fast"
        >
          <Icon 
            name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
            size={16} 
            className="text-text-secondary" 
          />
        </button>
      </div>

      <div className="space-y-3">
        <div>
          <p className="text-sm text-text-secondary mb-1">Balance</p>
          <p className="text-xl font-heading-semibold text-text-primary">
            {currency.balance.toLocaleString()} {currency.symbol}
          </p>
          <p className="text-sm text-text-secondary">
            ≈ ${currency.usdValue.toLocaleString('en-US', { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })}
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <Icon 
            name={getChangeIcon(currency.change24h)} 
            size={14} 
            className={getChangeColor(currency.change24h)} 
          />
          <span className={`text-sm font-body-medium ${getChangeColor(currency.change24h)}`}>
            {currency.change24h > 0 ? '+' : ''}{currency.change24h}% (24h)
          </span>
        </div>

        <div className="flex space-x-2">
          <button
            onClick={onDeposit}
            className="flex-1 flex items-center justify-center space-x-1 bg-primary text-white px-3 py-2 rounded-trading font-body-medium text-sm hover:bg-primary-600 transition-colors duration-trading-fast"
          >
            <Icon name="ArrowDownToLine" size={14} />
            <span>Deposit</span>
          </button>
          <button
            onClick={onWithdraw}
            className="flex-1 flex items-center justify-center space-x-1 bg-secondary-100 text-text-primary px-3 py-2 rounded-trading font-body-medium text-sm hover:bg-secondary-200 transition-colors duration-trading-fast"
          >
            <Icon name="ArrowUpFromLine" size={14} />
            <span>Withdraw</span>
          </button>
        </div>

        {isExpanded && (
          <div className="pt-4 border-t border-border space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-text-secondary mb-1">Network</p>
                <p className="text-text-primary font-body-medium">{currency.network}</p>
              </div>
              {!currency.isFiat && (
                <div>
                  <p className="text-text-secondary mb-1">Confirmations</p>
                  <p className="text-text-primary font-body-medium">{currency.confirmations}</p>
                </div>
              )}
              <div>
                <p className="text-text-secondary mb-1">Min Withdrawal</p>
                <p className="text-text-primary font-body-medium">
                  {currency.minWithdrawal} {currency.symbol}
                </p>
              </div>
              <div>
                <p className="text-text-secondary mb-1">
                  {currency.isFiat ? 'Processing Time' : 'Withdrawal Fee'}
                </p>
                <p className="text-text-primary font-body-medium">
                  {currency.isFiat 
                    ? currency.processingTime 
                    : `${currency.withdrawalFee} ${currency.symbol}`
                  }
                </p>
              </div>
            </div>

            {/* Quick Transfer to Trading */}
            <div className="space-y-2">
              <p className="text-sm font-body-medium text-text-secondary">
                Quick Transfer to Trading
              </p>
              <div className="flex space-x-2">
                <input
                  type="number"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  placeholder={`0.00 ${currency.symbol}`}
                  max={currency.balance}
                  className="flex-1 px-3 py-2 border border-border rounded-trading text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <button
                  onClick={handleTransfer}
                  disabled={!transferAmount || parseFloat(transferAmount) <= 0}
                  className="px-3 py-2 bg-accent text-white rounded-trading font-body-medium text-sm hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-trading-fast"
                >
                  Transfer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrencyCard;